// src/components/eventos/views/EventosDiretor.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';
import MetricasGerais from '../components/diretor/MetricasGerais';
import EventosRecentes from '../components/diretor/EventosRecentes';
import GraficoDesempenho from '../components/diretor/GraficoDesempenho';
import ControleEvento from '../components/diretor/ControleEvento';

/* ---- Ícones SVG ---- */
const ArrowLeftIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 18 9 12 15 6" />
    </svg>
);
const UserTieIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="7" r="4" /><path d="M12 11l-2 5 2 3 2-3-2-5" /><path d="M5.5 21a8 8 0 0 1 13 0" />
    </svg>
);
const PlayIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
        <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
);
const ChartIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
);
const CalendarIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
    </svg>
);
const WarningIcon = () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
);
const LogoutIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
    </svg>
);
const ChevronIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

const EventosDiretor = ({ supabase, session, clienteData, onLogout }) => {
    const navigate = useNavigate();
    const toast = useToast();
    const [eventos, setEventos] = useState([]);
    const [eventoAtivo, setEventoAtivo] = useState(null);
    const [proximoEvento, setProximoEvento] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [processando, setProcessando] = useState(false);
    const [activeTab, setActiveTab] = useState('controle');
    const [menuOpen, setMenuOpen] = useState(false);
    const primeiraExecucaoRef = useRef(true);

    const nomeUsuario = session?.user?.user_metadata?.nome || 'Usuário';
    const iniciais = nomeUsuario.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();

    const handleLogout = async () => {
        try { await onLogout(); } catch (e) { console.error(e); }
    };

    const fetchEventos = useCallback(async () => {
        if (primeiraExecucaoRef.current) setIsLoading(true);
        try {
            const clienteId = session.user.user_metadata?.cliente_id || clienteData?.id;
            let query = supabase.from('eventos').select('*').order('data', { ascending: true });
            if (clienteId) query = query.eq('cliente_id', clienteId);
            const { data, error } = await query;
            if (error) throw error;
            setEventos(data || []);
            const ativo = data?.find(e => e.status === 'em_andamento');
            const hoje = (() => { const _d=new Date(); return `${_d.getFullYear()}-${String(_d.getMonth()+1).padStart(2,'0')}-${String(_d.getDate()).padStart(2,'0')}`; })();
            const proximo = !ativo ? data?.find(e => e.status === 'agendado' && (e.data?.split('T')[0] || e.data) >= hoje) : null;
            setEventoAtivo(ativo || null);
            setProximoEvento(proximo || null);
        } catch (error) {
            toast({ title: 'Erro ao carregar dados', description: error.message, status: 'error', duration: 5000, isClosable: true });
        } finally {
            if (primeiraExecucaoRef.current) {
                setIsLoading(false);
                primeiraExecucaoRef.current = false;
            }
        }
    }, [supabase, session, toast, clienteData]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        fetchEventos();
        const interval = setInterval(fetchEventos, 30000);
        return () => clearInterval(interval);
    }, [fetchEventos]);

    const iniciarEvento = async (evento) => {
        setProcessando(true);
        try {
            const horaAtual = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
            const tarefasAtualizadas = evento.tarefas.map((t, idx) => ({ ...t, status: idx === 0 ? 'em_andamento' : 'pendente', horaInicioReal: idx === 0 ? horaAtual : null, horaFimReal: null }));
            const { error } = await supabase.from('eventos').update({ status: 'em_andamento', hora_inicio_real: horaAtual, tarefas: tarefasAtualizadas }).eq('id', evento.id);
            if (error) throw error;
            toast({ title: 'Evento iniciado', description: `${evento.titulo} está em andamento`, status: 'success', duration: 3000, isClosable: true });
            await fetchEventos();
        } catch (error) {
            toast({ title: 'Erro ao iniciar evento', description: error.message, status: 'error', duration: 5000, isClosable: true });
        } finally { setProcessando(false); }
    };

    const podeIniciar = (evento) => {
        if (!evento) return false;
        const agora = new Date();
        const hoje = `${agora.getFullYear()}-${String(agora.getMonth()+1).padStart(2,'0')}-${String(agora.getDate()).padStart(2,'0')}`;
        const eventoData = evento.data?.split('T')[0] || evento.data;
        if (eventoData > hoje) return false;
        const primeiraHora = evento.tarefas?.[0]?.horaInicio;
        if (!primeiraHora) return true;
        const horaAtual = `${agora.getHours().toString().padStart(2,'0')}:${agora.getMinutes().toString().padStart(2,'0')}`;
        return horaAtual >= primeiraHora;
    };

    const formatarData = (dataString) => {
        if (!dataString) return 'Data não informada';
        try {
            const datePart = dataString.split('T')[0];
            const [ano, mes, dia] = datePart.split('-');
            return `${dia}/${mes}/${ano}`;
        } catch { return 'Data inválida'; }
    };

    useEffect(() => {
        if (!menuOpen) return;
        const close = () => setMenuOpen(false);
        document.addEventListener('click', close);
        return () => document.removeEventListener('click', close);
    }, [menuOpen]);

    return (
        <>
            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }

                .ed-tab { cursor:pointer; padding:10px 20px; border-radius:10px; font-size:14px; font-weight:600; display:flex; align-items:center; gap:8px; transition:all 0.2s; border:none; background:none; color:#64748b; font-family:inherit; }
                .ed-tab:hover { color:#1e293b; background:#f8fafc; }
                .ed-tab.active { color:#0f172a; background:rgba(44,203,144,0.1); border:1px solid rgba(44,203,144,0.2); }

                .ed-btn-primary { background:linear-gradient(135deg,#2CCB90,#2DB8E1); border:none; border-radius:10px; color:#fff; font-weight:700; font-size:14px; padding:10px 20px; cursor:pointer; font-family:inherit; transition:all 0.2s; display:inline-flex; align-items:center; gap:8px; box-shadow:0 4px 14px rgba(44,203,144,0.2); }
                .ed-btn-primary:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 6px 20px rgba(44,203,144,0.3); }
                .ed-btn-primary:disabled { opacity:0.5; cursor:not-allowed; }
                .ed-btn-ghost { background:#f8fafc; border:1px solid #e2e8f0; border-radius:10px; color:#64748b; font-weight:500; font-size:14px; padding:10px 20px; cursor:pointer; font-family:inherit; transition:all 0.15s; display:inline-flex; align-items:center; gap:8px; }
                .ed-btn-ghost:hover { background:#f0fdf8; color:#1e293b; }

                .ed-menu-item { display:flex; align-items:center; gap:10px; width:100%; padding:10px 14px; border-radius:10px; font-size:13px; font-weight:500; color:#64748b; background:none; border:none; cursor:pointer; font-family:inherit; transition:all 0.15s; text-align:left; }
                .ed-menu-item:hover { background:#f8fafc; color:#1e293b; }
                .ed-menu-item.danger { color:#ef4444; }
                .ed-menu-item.danger:hover { background:rgba(239,68,68,0.06); }

                @media (max-width: 640px) { .ed-nav-email { display:none; } }
            `}</style>

            <div style={{ minHeight:'100vh', background:'#f0faf6', fontFamily:"'Inter', -apple-system, sans-serif" }}>

                {/* NAVBAR */}
                <header style={{ position:'sticky', top:0, zIndex:50, background:'rgba(255,255,255,0.9)', backdropFilter:'blur(24px)', WebkitBackdropFilter:'blur(24px)', borderBottom:'1px solid #e2e8f0' }}>
                    <div style={{ maxWidth:'1280px', margin:'0 auto', padding:'0 24px', height:'64px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'16px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'14px' }}>
                            <div style={{ display:'inline-flex', alignItems:'center', background:'#ffffff', borderRadius:'10px', padding:'6px 14px', boxShadow:'0 2px 8px rgba(0,0,0,0.08)', border:'1px solid #e2e8f0' }}>
                                <img src="/logo-horizontal.png" alt="Evlo" style={{ height:'26px', objectFit:'contain' }} onError={e => { e.target.style.display='none'; }} />
                            </div>
                            <button onClick={() => navigate('/eventos')} style={{ display:'flex', alignItems:'center', gap:'6px', padding:'7px 14px', borderRadius:'10px', background:'#f8fafc', border:'1px solid #e2e8f0', color:'#64748b', fontSize:'13px', fontWeight:500, cursor:'pointer', fontFamily:'inherit', transition:'all 0.15s', whiteSpace:'nowrap' }}
                                onMouseEnter={e => e.currentTarget.style.background='#f0fdf8'}
                                onMouseLeave={e => e.currentTarget.style.background='#f8fafc'}
                            >
                                <ArrowLeftIcon /> Voltar aos Eventos
                            </button>
                            {clienteData && (
                                <span style={{ padding:'4px 12px', borderRadius:'100px', background:'rgba(44,203,144,0.1)', border:'1px solid rgba(44,203,144,0.2)', color:'#2CCB90', fontSize:'11px', fontWeight:700 }}>
                                    {clienteData.nome}
                                </span>
                            )}
                        </div>

                        <div style={{ position:'relative' }}>
                            <button
                                onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
                                style={{ display:'flex', alignItems:'center', gap:'10px', padding:'6px 12px', borderRadius:'12px', background: menuOpen ? '#f8fafc' : '#ffffff', border:'1px solid #e2e8f0', cursor:'pointer', transition:'all 0.15s', fontFamily:'inherit' }}
                            >
                                <div style={{ width:'32px', height:'32px', borderRadius:'50%', background:'linear-gradient(135deg, #2CCB90, #2DB8E1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'12px', fontWeight:700, color:'#fff', flexShrink:0 }}>
                                    {iniciais}
                                </div>
                                <div className="ed-nav-email"><p style={{ margin:0, fontSize:'13px', fontWeight:600, color:'#0f172a' }}>{nomeUsuario}</p></div>
                                <span style={{ color:'#94a3b8', transition:'transform 0.2s', transform: menuOpen ? 'rotate(180deg)' : 'none' }}><ChevronIcon /></span>
                            </button>
                            {menuOpen && (
                                <div style={{ position:'absolute', top:'calc(100% + 8px)', right:0, minWidth:'190px', background:'#ffffff', border:'1px solid #e2e8f0', borderRadius:'14px', boxShadow:'0 16px 48px rgba(0,0,0,0.12)', padding:'8px', zIndex:100 }}>
                                    <button className="ed-menu-item" onClick={() => { navigate('/perfil'); setMenuOpen(false); }}>Meu Perfil</button>
                                    <div style={{ height:'1px', background:'#e2e8f0', margin:'6px 0' }} />
                                    <button className="ed-menu-item danger" onClick={handleLogout}><LogoutIcon /> Sair</button>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                {/* CONTEÚDO */}
                <main style={{ position:'relative', zIndex:1, maxWidth:'1280px', margin:'0 auto', padding:'32px 24px 80px' }}>

                    {/* Header */}
                    <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'28px' }}>
                        <div style={{ width:'40px', height:'40px', borderRadius:'12px', background:'linear-gradient(135deg, rgba(124,58,237,0.12), rgba(139,92,246,0.12))', border:'1px solid rgba(124,58,237,0.15)', display:'flex', alignItems:'center', justifyContent:'center', color:'#7c3aed' }}>
                            <UserTieIcon />
                        </div>
                        <div>
                            <h1 style={{ margin:0, fontSize:'clamp(20px,3vw,28px)', fontWeight:800, color:'#0f172a', letterSpacing:'-0.02em' }}>Visão do Diretor</h1>
                            <p style={{ margin:0, fontSize:'13px', color:'#64748b' }}>
                                {eventoAtivo ? 'Controlando evento em andamento' : 'Dashboard executivo com métricas e controle de eventos'}
                            </p>
                        </div>
                        {eventoAtivo && (
                            <span style={{ marginLeft:'auto', padding:'4px 12px', borderRadius:'100px', background:'rgba(44,203,144,0.1)', border:'1px solid rgba(44,203,144,0.25)', color:'#2CCB90', fontSize:'11px', fontWeight:700, letterSpacing:'0.05em', textTransform:'uppercase' }}>
                                Em Andamento
                            </span>
                        )}
                    </div>

                    {isLoading ? (
                        <div style={{ textAlign:'center', padding:'80px 0' }}>
                            <div style={{ width:'48px', height:'48px', borderRadius:'50%', border:'3px solid rgba(44,203,144,0.2)', borderTopColor:'#2CCB90', animation:'spin 0.8s linear infinite', margin:'0 auto 16px' }} />
                            <p style={{ color:'#94a3b8', fontSize:'14px', margin:0 }}>Carregando dados...</p>
                        </div>
                    ) : (
                        <>
                            {/* Tabs */}
                            <div style={{ display:'flex', gap:'6px', marginBottom:'24px', background:'#ffffff', border:'1px solid #e2e8f0', borderRadius:'14px', padding:'6px', width:'fit-content', boxShadow:'0 1px 3px rgba(0,0,0,0.05)' }}>
                                <button className={`ed-tab${activeTab === 'controle' ? ' active' : ''}`} onClick={() => setActiveTab('controle')}>
                                    <PlayIcon /> Controle de Eventos
                                    {eventoAtivo && <span style={{ padding:'2px 8px', borderRadius:'100px', background:'rgba(44,203,144,0.12)', color:'#2CCB90', fontSize:'10px', fontWeight:700 }}>ATIVO</span>}
                                </button>
                                <button className={`ed-tab${activeTab === 'analytics' ? ' active' : ''}`} onClick={() => setActiveTab('analytics')}>
                                    <ChartIcon /> Analytics
                                </button>
                            </div>

                            {/* Tab: Controle */}
                            {activeTab === 'controle' && (
                                <>
                                    {eventoAtivo ? (
                                        <div style={{ background:'#ffffff', border:'1px solid #e2e8f0', borderRadius:'16px', padding:'24px', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
                                            <ControleEvento evento={eventoAtivo} onAtualizarEvento={fetchEventos} supabase={supabase} clienteId={clienteData?.id} />
                                        </div>
                                    ) : proximoEvento ? (
                                        <div style={{ background:'rgba(44,203,144,0.05)', border:'1px solid rgba(44,203,144,0.18)', borderRadius:'16px', padding:'28px 32px' }}>
                                            <span style={{ display:'inline-flex', padding:'3px 10px', borderRadius:'100px', background:'rgba(44,203,144,0.1)', border:'1px solid rgba(44,203,144,0.22)', color:'#2CCB90', fontSize:'11px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'12px' }}>
                                                Próximo Evento Agendado
                                            </span>
                                            <h2 style={{ margin:'0 0 14px', fontSize:'22px', fontWeight:700, color:'#0f172a' }}>{proximoEvento.titulo}</h2>
                                            <div style={{ display:'flex', gap:'10px', flexWrap:'wrap', marginBottom:'14px' }}>
                                                <span style={{ display:'inline-flex', alignItems:'center', gap:'6px', padding:'4px 12px', borderRadius:'100px', background:'rgba(139,92,246,0.08)', border:'1px solid rgba(139,92,246,0.18)', color:'#7c3aed', fontSize:'12px', fontWeight:600 }}>
                                                    <CalendarIcon /> {formatarData(proximoEvento.data)}
                                                </span>
                                                <span style={{ display:'inline-flex', alignItems:'center', gap:'6px', padding:'4px 12px', borderRadius:'100px', background:'rgba(44,203,144,0.08)', border:'1px solid rgba(44,203,144,0.18)', color:'#2CCB90', fontSize:'12px', fontWeight:600 }}>
                                                    {proximoEvento.tarefas?.length || 0} tarefas
                                                </span>
                                            </div>
                                            {proximoEvento.descricao && <p style={{ margin:'0 0 20px', fontSize:'14px', color:'#64748b', lineHeight:'1.6' }}>{proximoEvento.descricao}</p>}
                                            {!podeIniciar(proximoEvento) && (
                                                <div style={{ display:'inline-flex', alignItems:'center', gap:'8px', padding:'8px 14px', borderRadius:'10px', background:'rgba(251,191,36,0.1)', border:'1px solid rgba(251,191,36,0.3)', color:'#d97706', fontSize:'13px', fontWeight:600, marginBottom:'16px' }}>
                                                    <CalendarIcon />
                                                    {(() => {
                                                        const hoje = (() => { const _d=new Date(); return `${_d.getFullYear()}-${String(_d.getMonth()+1).padStart(2,'0')}-${String(_d.getDate()).padStart(2,'0')}`; })();
                                                        const eventoData = proximoEvento.data?.split('T')[0] || proximoEvento.data;
                                                        const primeiraHora = proximoEvento.tarefas?.[0]?.horaInicio;
                                                        if (eventoData > hoje) return `Disponível em ${formatarData(proximoEvento.data)}${primeiraHora ? ` às ${primeiraHora}` : ''}`;
                                                        return `Disponível às ${primeiraHora}`;
                                                    })()}
                                                </div>
                                            )}
                                            <div style={{ display:'flex', gap:'12px', flexWrap:'wrap' }}>
                                                <button className="ed-btn-ghost" onClick={() => navigate(`/eventos/revisao/${proximoEvento.id}`)}>
                                                    <CalendarIcon /> Revisar Programa
                                                </button>
                                                <button className="ed-btn-primary" onClick={() => iniciarEvento(proximoEvento)} disabled={processando || !podeIniciar(proximoEvento)}>
                                                    <PlayIcon /> {processando ? 'Iniciando...' : 'Iniciar Evento'}
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ background:'#ffffff', border:'1px solid #e2e8f0', borderRadius:'16px', padding:'60px', textAlign:'center', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
                                            <div style={{ color:'#cbd5e1', margin:'0 auto 16px', width:'fit-content' }}><WarningIcon /></div>
                                            <p style={{ margin:'0 0 6px', fontSize:'18px', fontWeight:600, color:'#64748b' }}>Nenhum evento ativo ou agendado</p>
                                            <p style={{ margin:'0 0 20px', fontSize:'14px', color:'#94a3b8' }}>Crie um novo evento no Cadastro</p>
                                            <button className="ed-btn-primary" onClick={() => navigate('/eventos/cadastro')}>Ir para Cadastro</button>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Tab: Analytics */}
                            {activeTab === 'analytics' && (
                                <div style={{ display:'flex', flexDirection:'column', gap:'20px' }}>
                                    <MetricasGerais eventos={eventos} />
                                    <GraficoDesempenho eventos={eventos} />
                                    <EventosRecentes eventos={eventos} onRefresh={fetchEventos} />
                                </div>
                            )}
                        </>
                    )}
                </main>
            </div>
        </>
    );
};

export default EventosDiretor;
